// The rows of the fines queue. The order and every mark on a row come from
// fines_queue_v1; this lays them out. Used by the page and by the driver and
// owner panels, so a fine reads the same wherever it is seen.
import { AlertTriangle, History, Paperclip } from 'lucide-react'
import { Chip } from '../interviews/Chip'
import { cn } from '../../lib/shadcn/utils'
import { aartoLabel, firstProposal, formatZar, methodLabel, sourceLabel, statusLabel, statusTone, whenLabel, type QueueRow } from '../../utils/fines'

export function FinesList({
  rows,
  selected,
  onSelect,
  compact = false,
  empty,
}: {
  rows: QueueRow[]
  selected?: string | null
  onSelect?: (id: string) => void
  compact?: boolean
  empty: string
}) {
  if (rows.length === 0) {
    return <p className="px-4 py-6 text-center text-sm text-muted-foreground">{empty}</p>
  }
  return (
    <ul className="divide-y divide-border">
      {rows.map((r) => {
        const aarto = aartoLabel(r)
        const lead = firstProposal(r)
        const who = r.driver_name
          ? `Driver: ${r.driver_name}`
          : lead?.name
            ? `Proposed: ${lead.name}${r.proposed.filter((p) => p.driver_id).length > 1 ? ` and ${r.proposed.filter((p) => p.driver_id).length - 1} more` : ''}`
            : methodLabel(r.top_method)
        return (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => onSelect?.(r.id)}
              disabled={!onSelect}
              className={cn(
                'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors',
                onSelect && 'hover:bg-muted/60',
                selected === r.id && 'bg-[hsl(var(--brand-soft))]',
              )}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold text-foreground">{r.registration ?? 'No vehicle'}</span>
                <span className="truncate text-xs text-muted-foreground">{r.notice_number ?? (r.is_history ? '' : 'No notice number')}</span>
                <span className="ml-auto text-sm font-semibold tabular-nums text-foreground">{formatZar(r.amount_zar)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip tone={statusTone(r.status)}>{statusLabel(r.status)}</Chip>
                {aarto && (
                  <Chip tone="red" icon={<AlertTriangle className="h-3 w-3" />} className="whitespace-normal">
                    {aarto}
                  </Chip>
                )}
                {r.is_history && (
                  <Chip tone="neutral" icon={<History className="h-3 w-3" />}>
                    {sourceLabel(r.source)}
                  </Chip>
                )}
                {r.reversed && <Chip tone="amber">Reversed on the old sheet</Chip>}
                {r.has_evidence && <Paperclip className="h-3.5 w-3.5 text-muted-foreground" aria-label="Notice attached" />}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span>{whenLabel(r)}</span>
                <span>{who}</span>
                {!compact && r.owner_name && <span>Owner: {r.owner_name}</span>}
              </div>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
